import { DividerWithText, TaInput } from '@components/shared';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { decodeToken } from '@config/router/guards';
import { theme } from '@config/theme';
import { AuthHeader } from '@features/auth/AuthHeader';
import { CookieCard, SignUpPrompt } from '@features/auth/login';
import { loginSchema, loginValues, LoginValues } from '@features/auth/registration';
import { GoogleButton } from '@features/auth/registration/components/Common/GoogleButton';
import { AuthPepper } from '@features/AuthPaper';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import { useGoogleLogin } from '@react-oauth/google';
import { RoleEnum } from '@utils/util.types';
import { useFormik } from 'formik';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogin, useLoginWithGoogle } from 'services/auth';
import { useCheckUserCompletedRegistration } from 'services/users/usersHook';
import { mutate } from 'swr';

export const LoginForm = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const [showCookieCard, setShowCookieCard] = useState(false);
	const { login, isLoading } = useLogin();

	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const { loginWithGoogle } = useLoginWithGoogle();
	const { checkStatus, isLoading: checkStatusLoading } = useCheckUserCompletedRegistration();

	useEffect(() => {
		const cookieConsent = localStorage.getItem('cookieConsent');

		if (!cookieConsent) {
			const cookieTimer = setTimeout(() => {
				setShowCookieCard(true);
			}, 60000); // Show after 1 minute (60000ms)

			// Cleanup the timer if component unmounts
			return () => clearTimeout(cookieTimer);
		}
	}, []);

	useEffect(() => {
		Cookies.remove('accessToken');
		localStorage.removeItem('refreshToken');
	}, []);

	const handleSuccess = useCallback(
		async (credentials: any) => {
			const accessToken = credentials.access_token;
			const response = await loginWithGoogle(accessToken);

			if (response.error) {
				const errorMessage = t(response.error);
				showSnackbar(t('error'), errorMessage, 'error');

				return;
			}

			if (!response.accessToken) {
				showSnackbar('Error', 'No access token received', 'error');

				return;
			}

			// Check if user is admin by decoding the token
			const decodedToken = decodeToken(response.accessToken);
			if (decodedToken?.role.toLowerCase() === 'admin') {
				// Get current language for navigation
				const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
				const adminPath = `/${currentLanguage}/admin`;

				// Redirect to admin page
				setTimeout(() => {
					window.location.href = adminPath;
				}, 100);

				return;
			}

			// Then check registration status
			const data = await checkStatus();

			await mutate('users', undefined, {
				revalidate: false,
				populateCache: true,
			});

			if (!data?.user_completed_registration) {
				navigateWithLanguage(data.nextTab, {
					replace: true,
				});

				return;
			}

			// Only navigate to dashboard if registration is completed
			if (
				decodedToken?.role.toLowerCase() === RoleEnum.Specialist ||
				decodedToken?.role.toLowerCase() === RoleEnum.Company
			) {
				navigateWithLanguage('/specialist-switcher', {
					replace: true,
				});
			} else {
				navigateWithLanguage(`/${decodedToken?.role.toLowerCase()}/dashboard`, {
					replace: true,
				});
			}
		},
		[checkStatus, loginWithGoogle, navigateWithLanguage, showSnackbar]
	);

	const handleLogin = useCallback(
		async (values: LoginValues) => {
			const response = await login({
				email: values.email,
				password: values.password,
			});

			if (response.error) {
				const errorMessage = t(response.error);
				showSnackbar(t('error'), errorMessage, 'error');

				return;
			}

			if (!response.accessToken) {
				showSnackbar('Error', 'No access token received', 'error');

				return;
			}

			// Check if user is admin by decoding the token
			const decodedToken = decodeToken(response.accessToken);
			if (decodedToken?.role.toLowerCase() === 'admin') {
				// Get current language for navigation
				const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
				const adminPath = `/${currentLanguage}/admin`;

				// Redirect to admin page
				setTimeout(() => {
					window.location.href = adminPath;
				}, 100);

				return;
			}

			// Then check registration status
			const data = await checkStatus();

			await mutate('users', undefined, {
				revalidate: false,
				populateCache: true,
			});

			if (!data?.user_completed_registration) {
				navigateWithLanguage(data.nextTab, {
					replace: true,
				});

				return;
			}

			// Only navigate to dashboard if registration is completed
			if (
				decodedToken?.role.toLowerCase() === RoleEnum.Specialist ||
				decodedToken?.role.toLowerCase() === RoleEnum.Company
			) {
				navigateWithLanguage('/specialist-switcher', {
					replace: true,
				});
			} else {
				navigateWithLanguage(`/${decodedToken?.role.toLowerCase()}/dashboard`, {
					replace: true,
				});
			}
		},
		[checkStatus, login, navigateWithLanguage, showSnackbar]
	);

	const formik = useFormik<LoginValues>({
		initialValues: loginValues,
		validationSchema: loginSchema,
		onSubmit: handleLogin,
	});

	const { errors, touched } = formik;

	const renderEmailError = useMemo(() => {
		return (
			<FormHelperText
				sx={{
					display: 'flex',
					gap: '9px',
					marginBottom: '4px',
					marginTop: '10px',
					color: theme.palette.custom.red,
					font: 'roboto-regular',
					fontSize: '16px',
				}}
			>
				<ErrorOutlineOutlinedIcon />
				{t(errors.email || '')}
			</FormHelperText>
		);
	}, [errors.email, t]);

	const renderPasswordError = useMemo(() => {
		return (
			<FormHelperText
				sx={{
					display: 'flex',
					gap: '9px',
					marginBottom: '-30px',
					marginTop: '10px',
					color: theme.palette.custom.red,
					font: 'roboto-regular',
					fontSize: '16px',
				}}
			>
				<ErrorOutlineOutlinedIcon />
				{t(errors.password || '')}
			</FormHelperText>
		);
	}, [errors.password, t]);

	const googleLogin = useGoogleLogin({
		onSuccess: handleSuccess,
		flow: 'implicit',
	});

	const renderLoginError = useMemo(() => {
		if (formik.errors.password) {
			return renderPasswordError;
		} else if (formik.errors.email) {
			return renderEmailError;
		}
	}, [formik.errors.password, formik.errors.email, renderPasswordError, renderEmailError]);

	return (
		<AuthPepper withImage className="relative bg-center bg-no-repeat h-full w-full">
			<div className="max-w-xxl w-[330px] sm:!w-[448px]">
				<div className="mt-[50px]">
					<AuthHeader
						title={t('loginTitle')}
						spacing="mt-[40px] max-sm:mt-[29px]"
						wrapperSpacing="mb-[31px] max-sm:mb-[29px]"
					/>
				</div>
				<form className="mb-[80px] flex flex-col gap-5" onSubmit={formik.handleSubmit}>
					<div>
						<TaInput
							icon={<EmailOutlinedIcon />}
							id="email"
							label={t('email')}
							autoComplete="on"
							size="small"
							{...formik.getFieldProps('email')}
							error={!!errors.email}
							touched={touched.email}
						/>
					</div>
					<div>
						<TaInput
							icon={<LockOutlinedIcon />}
							id="password"
							label={t('password')}
							size="small"
							autoComplete="on"
							type="password"
							{...formik.getFieldProps('password')}
							error={!!(errors.password && touched.password)}
							touched={touched.password}
						/>
						{renderLoginError}
					</div>
					<span
						onClick={() => navigateWithLanguage('/login/reset')}
						className="flex justify-end text-secondary cursor-pointer roboto-bold underline"
					>
						{t('forgot')}
					</span>
					<Button
						disabled={isLoading || checkStatusLoading}
						color="primary"
						loading={isLoading || checkStatusLoading}
						variant="contained"
						fullWidth
						type="submit"
						sx={{ font: 'roboto-bold', fontSize: '14px' }}
					>
						{t('login')}
					</Button>
					<DividerWithText text={t('or')} widthClass="w-[100%]" />
					<div className=" flex justify-center">
						<GoogleButton
							disabled={isLoading || checkStatusLoading}
							handleClick={() => googleLogin()}
						/>
					</div>
					<SignUpPrompt />
				</form>
			</div>

			{showCookieCard && <CookieCard setShowCookieCard={setShowCookieCard} />}
		</AuthPepper>
	);
};
